import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { useAuth } from './AuthContext'
import { config } from '../config/env'
import { getUsableSessionAccessToken } from '../lib/sessionAccessToken'

export interface UsageData {
  clips_created: number
  clips_remaining: number
  clips_limit: number
  credits_used?: number
  credits_remaining?: number
  max_credits?: number
  plan: string | null
  subscription_status?: string | null
  month: string
  can_create_clips: boolean
  can_process_video?: boolean
}

export interface UsageDataError {
  kind: 'authentication' | 'request'
  message: string
}

interface UsageContextType {
  usage: UsageData | null
  loading: boolean
  error: UsageDataError | null
  refreshUsage: (forceClearCache?: boolean) => Promise<void>
  beginUsageConfirmation: (ownerId: string | undefined) => (usage: UsageData) => boolean
  getMaxClips: () => Promise<number>
}

const UsageContext = createContext<UsageContextType | undefined>(undefined)

export const useUsage = () => {
  const context = useContext(UsageContext)
  if (!context) {
    throw new Error('useUsage must be used within a UsageProvider')
  }
  return context
}

interface UsageProviderProps {
  children: React.ReactNode
  effectsEnabled?: boolean
}

const authenticationError = (): UsageDataError => ({
  kind: 'authentication',
  message:
    'Your sign-in session is unavailable or expired. Sign in again to load your plan and allowance.',
})

const requestError = (): UsageDataError => ({
  kind: 'request',
  message:
    'Your plan and allowance are temporarily unavailable. Retry in a moment.',
})

const responseError = (status: number): UsageDataError =>
  status === 401 || status === 403
    ? authenticationError()
    : requestError()

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value)

const isUsageData = (value: unknown): value is UsageData => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const candidate = value as Record<string, unknown>
  return (
    isFiniteNumber(candidate.clips_created) &&
    isFiniteNumber(candidate.clips_remaining) &&
    isFiniteNumber(candidate.clips_limit) &&
    (typeof candidate.plan === 'string' || candidate.plan === null) &&
    (candidate.subscription_status === undefined ||
      candidate.subscription_status === null ||
      typeof candidate.subscription_status === 'string') &&
    typeof candidate.month === 'string' &&
    typeof candidate.can_create_clips === 'boolean' &&
    (candidate.credits_used === undefined ||
      isFiniteNumber(candidate.credits_used)) &&
    (candidate.credits_remaining === undefined ||
      isFiniteNumber(candidate.credits_remaining)) &&
    (candidate.max_credits === undefined ||
      isFiniteNumber(candidate.max_credits)) &&
    (candidate.can_process_video === undefined ||
      typeof candidate.can_process_video === 'boolean')
  )
}

export const usageFromResponse = (
  payload: unknown,
  forceClearCache: boolean
): UsageData | null => {
  const candidate =
    forceClearCache &&
    payload &&
    typeof payload === 'object' &&
    !Array.isArray(payload)
      ? (payload as Record<string, unknown>).usage
      : payload
  return isUsageData(candidate) ? candidate : null
}

interface UsageFetchRequest {
  ownerId: string
  generation: number
  key: object
  promise: Promise<void>
}

export const UsageProvider: React.FC<UsageProviderProps> = ({
  children,
  effectsEnabled = true,
}) => {
  const { user, session } = useAuth()
  const currentOwnerId = user?.id ?? null
  const [stateOwnerId, setStateOwnerId] = useState<string | null>(
    currentOwnerId
  )
  const [storedUsage, setUsage] = useState<UsageData | null>(null)
  const [storedLoading, setLoading] = useState(false)
  const [storedError, setError] = useState<UsageDataError | null>(null)
  const refreshUsagePromiseRef = useRef<UsageFetchRequest | null>(null)
  const activeOwnerRef = useRef<string | null>(currentOwnerId)
  const ownerGenerationRef = useRef(0)

  const stateMatchesOwner = stateOwnerId === currentOwnerId
  const usage = stateMatchesOwner ? storedUsage : null
  const loading = stateMatchesOwner ? storedLoading : false
  const error = stateMatchesOwner ? storedError : null

  const isActiveOwnerRequest = useCallback(
    (ownerId: string, generation: number) =>
      activeOwnerRef.current === ownerId &&
      ownerGenerationRef.current === generation,
    []
  )

  // Capture ownership before the authenticated confirmation begins, including
  // sign-out/sign-in cycles that return to the same user ID.
  const beginUsageConfirmation = useCallback((ownerId: string | undefined) => {
    const generation = ownerGenerationRef.current
    return (confirmed: UsageData) => {
      if (!ownerId || !isActiveOwnerRequest(ownerId, generation) || !isUsageData(confirmed)) return false
      // Older cached reads must not overwrite this confirmed server response.
      refreshUsagePromiseRef.current = null
      setUsage(confirmed)
      setError(null)
      setLoading(false)
      return true
    }
  }, [isActiveOwnerRequest])

  const requestUsage = useCallback(
    async (
      forceClearCache: boolean,
      showLoading: boolean
    ): Promise<void> => {
      const ownerId = user?.id
      const generation = ownerGenerationRef.current
      if (!ownerId || !isActiveOwnerRequest(ownerId, generation)) return

      const accessToken = getUsableSessionAccessToken(session)
      if (!accessToken) {
        if (isActiveOwnerRequest(ownerId, generation)) {
          setError(authenticationError())
          setLoading(false)
        }
        return
      }

      const activeRequest = refreshUsagePromiseRef.current
      if (
        activeRequest?.ownerId === ownerId &&
        activeRequest.generation === generation
      ) {
        return activeRequest.promise
      }

      const requestKey = {}
      const requestRecord: UsageFetchRequest = {
        ownerId,
        generation,
        key: requestKey,
        promise: Promise.resolve(),
      }
      refreshUsagePromiseRef.current = requestRecord
      const isCurrentRequest = () =>
        isActiveOwnerRequest(ownerId, generation) &&
        refreshUsagePromiseRef.current?.key === requestKey

      const refreshPromise = (async () => {
        try {
          if (isCurrentRequest()) {
            if (showLoading) setLoading(true)
            setError(null)
          }

          const response = await fetch(
            forceClearCache
              ? config.endpoints.refreshCache(ownerId)
              : config.endpoints.usage(ownerId, ''),
            {
              method: forceClearCache ? 'POST' : 'GET',
              headers: {
                Accept: 'application/json',
                Authorization: `Bearer ${accessToken}`,
              },
            }
          )

          if (!response.ok) {
            if (isCurrentRequest()) {
              setError(responseError(response.status))
            }
            return
          }

          const nextUsage = usageFromResponse(
            await response.json(),
            forceClearCache
          )
          if (!nextUsage) {
            throw new Error('Invalid usage response')
          }

          if (isCurrentRequest()) {
            setUsage(nextUsage)
            setError(null)
          }
        } catch {
          if (isCurrentRequest()) {
            setError(requestError())
          }
        } finally {
          if (
            isCurrentRequest() &&
            showLoading
          ) {
            setLoading(false)
          }
          if (refreshUsagePromiseRef.current?.key === requestKey) {
            refreshUsagePromiseRef.current = null
          }
        }
      })()

      requestRecord.promise = refreshPromise
      return refreshPromise
    },
    [isActiveOwnerRequest, session, user?.id]
  )

  const refreshUsage = useCallback(
    async (forceClearCache = false) => {
      await requestUsage(forceClearCache, true)
    },
    [requestUsage]
  )

  const getMaxClips = useCallback(async (): Promise<number> => {
    const ownerId = user?.id
    const generation = ownerGenerationRef.current
    const accessToken = getUsableSessionAccessToken(session)
    if (
      !ownerId ||
      !isActiveOwnerRequest(ownerId, generation) ||
      !accessToken
    ) {
      if (
        ownerId &&
        isActiveOwnerRequest(ownerId, generation)
      ) {
        setError(authenticationError())
      }
      throw new Error('Authenticated session required')
    }

    try {
      const response = await fetch(config.endpoints.maxClips(ownerId), {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      })

      if (!response.ok) {
        if (isActiveOwnerRequest(ownerId, generation)) {
          setError(responseError(response.status))
        }
        throw new Error('Maximum clips unavailable')
      }

      const payload = await response.json()
      if (
        !payload ||
        !Number.isInteger(payload.max_clips) ||
        payload.max_clips < 0
      ) {
        throw new Error('Invalid maximum clips response')
      }

      if (!isActiveOwnerRequest(ownerId, generation)) {
        throw new Error('Authenticated owner changed')
      }

      setError(null)
      return payload.max_clips
    } catch (caughtError) {
      if (isActiveOwnerRequest(ownerId, generation)) {
        setError((currentError) => currentError ?? requestError())
      }
      throw caughtError
    }
  }, [isActiveOwnerRequest, session, user?.id])

  useLayoutEffect(() => {
    activeOwnerRef.current = currentOwnerId
    ownerGenerationRef.current += 1
    refreshUsagePromiseRef.current = null
    setStateOwnerId(currentOwnerId)
    setUsage(null)
    setLoading(false)
    setError(null)
  }, [currentOwnerId, effectsEnabled])

  useEffect(() => {
    if (!effectsEnabled) return

    if (!currentOwnerId) return

    if (!getUsableSessionAccessToken(session)) {
      const generation = ownerGenerationRef.current
      if (isActiveOwnerRequest(currentOwnerId, generation)) {
        setError(authenticationError())
        setLoading(false)
      }
      return
    }

    void requestUsage(false, true)
    const intervalId = window.setInterval(() => {
      void requestUsage(false, false)
    }, 60_000)

    return () => window.clearInterval(intervalId)
  }, [
    currentOwnerId,
    effectsEnabled,
    isActiveOwnerRequest,
    requestUsage,
    session,
  ])

  return (
    <UsageContext.Provider
      value={{
        usage,
        loading,
        error,
        refreshUsage,
        beginUsageConfirmation,
        getMaxClips,
      }}
    >
      {children}
    </UsageContext.Provider>
  )
}
